import React, { useMemo, useState } from 'react'

import {
  FormattedWidgetData,
  FormattedWidgetDataDataset,
  ParsedWidgetDataLabel,
  TReportWidgetFullyLoaded,
  TWidgetAuditLog
} from 'types/GlobalWidget'
import { TEditLockedWidgetBody } from 'types/GlobalReport'
import { TCommentThreadData } from 'types/GlobalComment'

type MenuState = {
  event: React.MouseEvent<Element, MouseEvent> | PointerEvent | null
  open: boolean
  kind: 'segment' | 'key_figure'
  segments: ParsedWidgetDataLabel[]
  datasetIndex: number | undefined
  kpiOptionId: string | null
}

const INITIAL_MENU: MenuState = {
  event: null,
  open: false,
  kind: 'segment',
  segments: [],
  datasetIndex: undefined,
  kpiOptionId: null
}

type UseSegmentStateParams = {
  editionId: string | null
  formattedData: FormattedWidgetData | null
  onAddComment?: (widgetId: string, data: TCommentThreadData) => void
  tryEditDataReportWidget: (
    id: string,
    editionId: string,
    body: TEditLockedWidgetBody
  ) => void
  widget: TReportWidgetFullyLoaded | null
  widgetId: string
  widgetTitle: string | null
}

function filterKeyFigureAuditLogs(
  auditLogs: TWidgetAuditLog[],
  kpiOptionIndex: number
): { original: number | null; edited: number | null } {
  const logs = auditLogs.filter(
    ({ changes }) => changes.kpi_option_index === kpiOptionIndex
  )

  if (logs.length === 0) return { original: null, edited: null }

  return {
    original: logs[0].changes.original_value,
    edited: logs[logs.length - 1].changes.new_value
  }
}

function filterAuditLogs(
  auditLogs: TWidgetAuditLog[],
  clickedSegment: ParsedWidgetDataLabel | undefined,
  clickedSegmentDataset: FormattedWidgetDataDataset | null
): TWidgetAuditLog[] {
  if (!clickedSegment || !clickedSegmentDataset) return []

  return auditLogs.filter(({ changes }) => {
    return (
      changes.column_label?.attribute_option_id ===
        clickedSegment.attribute_option_id &&
      changes.column_label?.label === clickedSegment.label &&
      String(changes.breakdown_label) === clickedSegmentDataset.label
    )
  })
}

function buildBreakdownEditBody(
  formattedData: FormattedWidgetData,
  datasetIndex: number | undefined,
  clickedSegment: ParsedWidgetDataLabel,
  newValue: number
): TEditLockedWidgetBody | null {
  const formattedDataset = formattedData.datasets[datasetIndex ?? 0]

  if (!formattedDataset) return null

  return {
    edit: {
      type: 'breakdown',
      row_index: formattedDataset.index,
      data_index: clickedSegment.index,
      new_value: newValue
    },
    reason: null
  }
}

export function useWidgetContextMenu({
  editionId,
  formattedData,
  onAddComment,
  tryEditDataReportWidget,
  widget,
  widgetId,
  widgetTitle
}: UseSegmentStateParams) {
  const [contextMenu, setContextMenu] = useState<MenuState>(INITIAL_MENU)
  const [editPopover, setEditPopover] = useState(false)

  const clickedSegment = contextMenu.segments[0]
  const clickedSegmentDataset =
    formattedData?.datasets[contextMenu.datasetIndex ?? 0] ?? null

  const clickedSegmentAuditLogs = useMemo(
    () =>
      filterAuditLogs(
        widget?.audit_logs ?? [],
        clickedSegment,
        clickedSegmentDataset
      ),
    [widget, clickedSegment, clickedSegmentDataset]
  )

  const handleClickSegment = (
    event: React.MouseEvent<HTMLElement, MouseEvent> | PointerEvent | null,
    segments: ParsedWidgetDataLabel[],
    datasetIndex?: number
  ) => {
    setContextMenu({
      event,
      open: true,
      kind: 'segment',
      segments,
      datasetIndex,
      kpiOptionId: null
    })
  }

  const handleKeyFigureClick = (
    event: React.MouseEvent,
    kpiOptionId: string
  ) => {
    setContextMenu({
      event,
      open: true,
      kind: 'key_figure',
      segments: [],
      datasetIndex: undefined,
      kpiOptionId
    })
  }

  const closeMenu = () =>
    setContextMenu((prev) => ({ ...prev, open: false }))

  const handleEdit = (newValue: number) => {
    if (!editionId || !widget) return

    if (contextMenu.kind === 'key_figure') {
      if (!contextMenu.kpiOptionId) return

      const kpiIndex =
        widget.data.values.find(
          (v) => v.kpi_option_id === contextMenu.kpiOptionId
        )?.index ?? 0

      tryEditDataReportWidget(widgetId, editionId, {
        edit: { type: 'key_figure', kpi_option_index: kpiIndex, new_value: newValue },
        reason: null
      })
    } else {
      if (!formattedData || !clickedSegment) return

      const body = buildBreakdownEditBody(
        formattedData,
        contextMenu.datasetIndex,
        clickedSegment,
        newValue
      )

      if (!body) return

      tryEditDataReportWidget(widgetId, editionId, body)
    }
  }

  const clickedSegmentValue =
    clickedSegment && clickedSegmentDataset
      ? (clickedSegmentDataset.rawData[clickedSegment.index] as number)
      : null

  const handleAddComment = () => {
    if (!onAddComment) return

    if (contextMenu.kind === 'key_figure') {
      const kpiOption = widget?.settings.kpi_options.find(
        (o) => o.id === contextMenu.kpiOptionId
      )
      const kpiValue = widget?.data.values.find(
        (v) => v.kpi_option_id === contextMenu.kpiOptionId
      )

      if (!kpiOption || kpiValue === undefined) return

      onAddComment(widgetId, {
        datasetLabel: widgetTitle ?? '',
        label: kpiOption.title,
        value: kpiValue.value
      })
    } else {
      if (!clickedSegment || clickedSegmentValue === null) return

      onAddComment(widgetId, {
        datasetLabel: widgetTitle ?? '',
        label: clickedSegment.display_label,
        value: clickedSegmentValue
      })
    }
  }

  const editPopoverValue = useMemo(() => {
    if (contextMenu.kind === 'key_figure') {
      const kpiValue = widget?.data.values.find(
        (v) => v.kpi_option_id === contextMenu.kpiOptionId
      )
      const auditValues = filterKeyFigureAuditLogs(
        widget?.audit_logs ?? [],
        kpiValue?.index ?? 0
      )

      return {
        edited: auditValues.edited,
        original: auditValues.original ?? kpiValue?.value ?? 0
      }
    }

    const auditOriginal =
      clickedSegmentAuditLogs.length > 0
        ? clickedSegmentAuditLogs[0].changes.original_value
        : null
    const auditEdited =
      clickedSegmentAuditLogs.length > 0
        ? clickedSegmentAuditLogs[clickedSegmentAuditLogs.length - 1].changes
          .new_value
        : null

    return {
      edited: auditEdited,
      original: auditOriginal ?? clickedSegmentValue ?? 0
    }
  }, [contextMenu, widget, clickedSegmentAuditLogs, clickedSegmentValue])

  return {
    contextMenu,
    handleAddComment,
    handleClickSegment,
    handleKeyFigureClick,
    closeMenu,
    editPopover,
    setEditPopover,
    handleEdit,
    editPopoverValue,
    clickedSegmentLabel: clickedSegment?.display_label,
    editSegmentPopoverKey: `${clickedSegment?.attribute_option_id}-${clickedSegment?.label}-${contextMenu.datasetIndex}`
  }
}
